import { z } from 'zod';

const envSource = {
  ALGOLIA_API_KEY: process.env.ALGOLIA_API_KEY,
  ALGOLIA_APPLICATION_ID: process.env.ALGOLIA_APPLICATION_ID ?? 'testingJLK3D8UND2',
  ALGOLIA_INDEX_NAME: process.env.ALGOLIA_INDEX_NAME ?? 'nmdpsite-qa',
  ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME: process.env.ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME,
  ALGOLIA_REPORTS_INDEX_NAME: process.env.ALGOLIA_REPORTS_INDEX_NAME,
  ALGOLIA_REPORTS_API_KEY: process.env.ALGOLIA_REPORTS_API_KEY,
  ALGOLIA_SERVER_RENDER_DISABLED: process.env.ALGOLIA_SERVER_RENDER_DISABLED,
  ANONYMOUS_ROLE: process.env.ANONYMOUS_ROLE,
  APP_NAME: process.env.OTEL_SERVICE_NAME,
  CDP_API_TOKEN: process.env.CDP_API_TOKEN,
  CDP_CLIENT_KEY: process.env.CDP_CLIENT_KEY,
  CDP_ENGAGE_TARGET: process.env.CDP_ENGAGE_TARGET,
  CDP_POINT_OF_SALE: process.env.CDP_POINT_OF_SALE,
  CDP_TARGET: process.env.CDP_TARGET,
  FEATURE_FLAG_ENABLE_ENGAGE_SDK: process.env.FEATURE_FLAG_ENABLE_ENGAGE_SDK,
  FEATURE_FLAG_ENABLE_ONENORTH_DEMO: process.env.FEATURE_FLAG_ENABLE_ONENORTH_DEMO,
  ISR_REVALIDATE_TIMER: process.env.ISR_REVALIDATE_TIMER,
  JSS_APP_NAME: process.env.JSS_APP_NAME,
  JSS_APP_NAME_NO_PREVIEW: process.env.JSS_APP_NAME,
  JSS_EDITING_SECRET: process.env.JSS_EDITING_SECRET,
  MEDIA_API_KEYS: process.env.MEDIA_API_KEYS,
  MEDIA_FANCY_ERRORS: process.env.MEDIA_FANCY_ERRORS,
  NEXT_PUBLIC_RUM_ENABLED: process.env.NEXT_PUBLIC_RUM_ENABLED,
  NEXT_PUBLIC_DYNATRACE_TENANT_ID: process.env.NEXT_PUBLIC_DYNATRACE_TENANT_ID ?? '176fb25782e',
  NEXT_PUBLIC_DYNATRACE_APPLICATION_ID:
    process.env.NEXT_PUBLIC_DYNATRACE_APPLICATION_ID ?? 'bf39931tga',
  NEXT_PUBLIC_DYNATRACE_INSTANCE_ID:
    process.env.NEXT_PUBLIC_DYNATRACE_INSTANCE_ID ?? 'fa750048aecb7b35',
  NEXT_PUBLIC_HIGHLIGHT_PROJECT_ID: process.env.NEXT_PUBLIC_HIGHLIGHT_PROJECT_ID,
  NEXTAUTH_DEBUG: process.env.NEXTAUTH_DEBUG,
  NEXTAUTH_SECRET: process.env.NEXTAUTH_SECRET,
  NEXTAUTH_URL: process.env.NEXTAUTH_URL,
  OKTA_APITOKEN: process.env.OKTA_APITOKEN,
  OKTA_CLAIM: process.env.OKTA_CLAIM,
  OKTA_CLAIM_CENTERS: process.env.OKTA_CLAIM_CENTERS,
  OKTA_CLIENTID: process.env.OKTA_CLIENTID,
  OKTA_CLIENTSECRET: process.env.OKTA_CLIENTSECRET,
  OKTA_ISSUER: process.env.OKTA_ISSUER,
  OKTA_SCOPE: process.env.OKTA_SCOPE,
  PUBLIC_HYPERDX_API_KEY: process.env.PUBLIC_HYPERDX_API_KEY,
  PUBLIC_URL: process.env.PUBLIC_URL,
  SEARCH_CRAWLER_PASSWORD: process.env.SEARCH_CRAWLER_PASSWORD,
  SESSION_TIMEOUT: process.env.SESSION_TIMEOUT,
  SITECORE_API_HOST: process.env.SITECORE_API_HOST,
  SITECORE_API_KEY: process.env.SITECORE_API_KEY,
  SITECORE_API_KEY_REPORTS: process.env.SITECORE_API_KEY_REPORTS,
  SKIP_AUTHENTICATION: process.env.SKIP_AUTHENTICATION,
  VERCEL: process.env.VERCEL,
  VERCEL_FORCE_NODEJS_STREAMING: process.env.VERCEL_FORCE_NODEJS_STREAMING,
};

const guidMatch = /^\s*[{(]?([\dA-F]{8}-?(?:[\dA-F]{4}-?){3}[\dA-F]{12})[)}]?\s*$/i;

const preprocessors = {
  guid: (x: unknown, ctx: z.RefinementCtx) => {
    const check = typeof x === 'string' ? x : '';
    const match = guidMatch.exec(check);
    if (!match || match.length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Must be a GUID (input ${x} ${JSON.stringify(match)})`,
        fatal: true,
      });
      return z.NEVER;
    }
    return `{${match[1]}}`;
  },
  numericEnvVariable: (x: unknown) => {
    if (typeof x === 'string') {
      const check = x?.trim();
      if (check) {
        return Number(check);
      }
    }
    return x;
  },
  trimmedOptionalString: (x: unknown) => {
    if (typeof x === 'string') {
      const v = x.trim();
      return v ? v : undefined;
    }
    return x;
  },
  trimmedOptionalLowercaseString: (x: unknown) => {
    if (typeof x === 'string') {
      const v = x.trim().toLocaleLowerCase();
      return v ? v : undefined;
    }
    return x;
  },
  truthyBool: (x: unknown) => (typeof x === 'string' ? x === '1' || x === 'true' : x),
  truthyBoolDefaultTrue: (x: unknown) =>
    typeof x === 'string' && x.trim().length > 0 ? x === '1' || x === 'true' : true,
  urlWithTrailingSlash: (x: unknown) => {
    if (typeof x === 'string') {
      return !x.endsWith('/') ? `${x}/` : x;
    }
    return x;
  },
  // @ts-ignore - not sure why this needs to be here
  urlWithoutTrailingSlash: (x: unknown) => {
    if (typeof x === 'string') {
      return x.endsWith('/') ? x.substring(0, x.length - 1) : x;
    }
    return x;
  },
};
const schema = <
  // We need to use any here to match with ZodTypeDef
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TOutputType = any,
  TServerDef extends z.ZodTypeDef = z.ZodTypeDef,
  TInputType = TOutputType
>(
  serverSchema: z.ZodType<TOutputType, TServerDef, TInputType>
): z.ZodType<TOutputType, TServerDef, TInputType> =>
  /*
  | z.ZodOptional<z.ZodType<TOutputType, TServerDef, TInputType>>
  */
  typeof window === 'undefined'
    ? serverSchema
    : (serverSchema.optional() as unknown as z.ZodType<TOutputType, TServerDef, TInputType>);
/**
 * Environment configuration schema using Zod for validation.
 *
 * @property {string} ALGOLIA_APPLICATION_ID - Algolia Application Id.
 * @property {string} ALGOLIA_INDEX_NAME - Algolia index.
 * @property {string} ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME - Algolia suggestion index.
 * @property {string} ALGOLIA_REPORTS_INDEX_NAME - Algolia reports index.
 * @property {string} ANONYMOUS_ROLE - Role used for anonymous website access.
 * @property {string} APP_NAME - App name used in observability logs.
 * @property {string} CDP_CLIENT_KEY - Personalize client key.
 * @property {string} CDP_API_TOKEN - Personalize API TOKEN.
 * @property {string} CDP_TARGET - Personalize API target URL.
 * @property {string} CDP_ENGAGE_TARGET - Personalize SDK target URL.
 * @property {string} CDP_POINT_OF_SALE - Personalize Point of Sale.
 * @property {boolean} FEATURE_FLAG_ENABLE_ENGAGE_SDK - When turned on the Boxover integration will be disabled and the Engage SDK will be used instead.
 * @property {boolean} FEATURE_FLAG_ENABLE_ONENORTH_DEMO - When turned on the OneNorth demo features will be enabled.
 * @property {number} ISR_REVALIDATE_TIMER - Number of seconds before Incremental Site Regeneration cache expires.
 * @property {string} JSS_EDITING_SECRET - Secret used to authenticate requests from content management server.
 * @property {Record<string, string>} MEDIA_API_KEYS - Media library items retrieved with role-specific API keys.
 * @property {boolean} MEDIA_FANCY_ERRORS - When site error pages are loaded on the browser (keeping the URL static).
 * @property {boolean} NEXTAUTH_DEBUG - Whether or not NextAuth debug mode is enabled.
 * @property {boolean} NEXTAUTH_SECRET - Secret used to encrypt/decrypt nextauth tokens.
 * @property {string} NEXTAUTH_URL - Base URL for NextAuth.
 * @property {boolean} NEXT_PUBLIC_RUM_ENABLED - Whether Dynatrace RUM is enabled.
 * @property {string} NEXT_PUBLIC_DYNATRACE_TENANT_ID - Dynatrace Tenant ID.
 * @property {string} NEXT_PUBLIC_DYNATRACE_APPLICATION_ID - Dynatrace Application ID.
 * @property {string} NEXT_PUBLIC_DYNATRACE_INSTANCE_ID - Dynatrace Instance ID.
 * @property {string} NEXT_PUBLIC_HIGHLIGHT_PROJECT_ID - Highlight Observability Project ID.
 * @property {string} OKTA_BASEURL - Base URL for Okta.
 * @property {string} OKTA_CLAIM - Name of the claim used to authorize website access.
 * @property {string} OKTA_CLIENTID - Okta client ID.
 * @property {string} OKTA_CLIENTSECRET - Okta client secret.
 * @property {string} OKTA_CLAIM_CENTERS - Name of the claim containing center data.
 * @property {string} OKTA_ISSUER - Okta issuing endpoint.
 * @property {string} OKTA_SCOPE - Okta authentication scope.
 * @property {string} PUBLIC_HYPERDX_API_KEY - Highlight Observability API key.
 * @property {string} PUBLIC_URL - Public URL for the site.
 * @property {string} SEARCH_CRAWLER_PASSWORD - Password the search bot can use to authenticate.
 * @property {string} JSS_APP_NAME - Packaged app name.
 * @property {boolean} SKIP_AUTHENTICATION - Whether or not authentication is enabled for this site.
 * @property {number} SESSION_TIMEOUT - Session timeout in minutes.
 * @property {string} SITECORE_API_HOST - Base URL of Sitecore API host, always ends with a trailing slash.
 * @property {string} SITECORE_API_KEY - Sitecore API key, must be a GUID value.
 * @property {string} SITECORE_API_KEY_REPORTS - Sitecore API key for reports, must be a GUID value.
 */
export const env = z
  .object({
    /**
     * Algolia API Key Id
     */
    ALGOLIA_API_KEY: schema(z.string()),
    /**
     * Algolia Application Id
     */
    ALGOLIA_APPLICATION_ID: schema(z.string().default('testingJLK3D8UND2')),
    /**
     * Algolia index
     */
    ALGOLIA_INDEX_NAME: schema(z.string().default('nmdpsite-qa')),
    /**
     * Algolia suggestion index
     */
    ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME: schema(
      z
        .string()
        .default(`${envSource.ALGOLIA_INDEX_NAME}_suggestions`)
        .transform((x) =>
          !!x ? x.toLocaleLowerCase() : `${envSource.ALGOLIA_INDEX_NAME}_suggestions`
        )
    ),
    /**
     * Algolia reports index
     */
    ALGOLIA_REPORTS_INDEX_NAME: schema(
      z.preprocess(preprocessors.trimmedOptionalLowercaseString, z.string().optional())
    ),

    /**
     * Algolia reports API key
     */
    ALGOLIA_REPORTS_API_KEY: schema(
      z.preprocess(preprocessors.trimmedOptionalString, z.string().optional())
    ),

    /**
     * When true, serer-side rendering of page content will be disabled. This is achieved by passing no server-side state
     * into Aloglia's warpping component, which int tunr causes it to hide its children.
     */
    ALGOLIA_SERVER_RENDER_DISABLED: schema(
      z.preprocess(preprocessors.truthyBool, z.boolean().default(false))
    ),
    /**
     * Role used for anonymous website access
     */
    ANONYMOUS_ROLE: z.preprocess(preprocessors.trimmedOptionalString, z.string().optional()),
    /**
     * App name used in observability logs
     */
    APP_NAME: z.preprocess((x, ctx) => {
      if (typeof x === 'string' && x) {
        return x;
      }
      const appUrlMatch = /https?:\/\/([^/:]+)/i.exec(envSource.PUBLIC_URL ?? '');
      if (!appUrlMatch) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Must have public url or app observability name',
          fatal: true,
        });
        return z.NEVER;
      }
      return `sitecore-${appUrlMatch[1].toLocaleLowerCase()}`;
    }, z.string().min(1)),
    /**
     * Personalize client key
     */
    CDP_CLIENT_KEY: schema(z.string().default('nmduatawv6g974ig49qf7suh5wafcvl7')),
    /**
     * Personalize API TOKEN.  OK to keep in source control - it's sent down to the client
     * on render anyways
     */
    CDP_API_TOKEN: schema(z.string().default('9EqExvT980b27xNjgdNnpoUpEisEqroa')),
    /**
     * Personalize API target URL.  See https://doc.sitecore.com/cdp/en/developers/api/settings-object.html
     */
    CDP_TARGET: schema(z.string().default('https://api-us.sitecorecloud.io')),
    /**
     * Personalize SDK target URL. Only used if FEATURE_FLAG_ENABLE_ENGAGE_SDK is true
     */
    CDP_ENGAGE_TARGET: schema(z.string().default('https://api-engage-us.sitecorecloud.io')),
    /**
     * Personalize Point of Sale.  See https://doc.sitecore.com/cdp/en/developers/api/settings-object.html
     */
    CDP_POINT_OF_SALE: schema(z.string().default('BeConnected-local')),
    /**
     * When turned on the Boxover integration will be disabled and the Engage SDK will be used instead.
     */
    FEATURE_FLAG_ENABLE_ENGAGE_SDK: schema(
      z.preprocess(preprocessors.truthyBool, z.boolean().default(false))
    ),
    /**
     * When turned on the OneNorth demo hero carousel will be used.  IMPORTANT: THIS IS NOT PROD READY
     */
    FEATURE_FLAG_ENABLE_ONENORTH_DEMO: schema(
      z.preprocess(preprocessors.truthyBool, z.boolean().default(false))
    ),
    /**
     * Number of seconds before Incremental Site Regeneration cache expires
     */
    ISR_REVALIDATE_TIMER: schema(
      z.preprocess(preprocessors.numericEnvVariable, z.number().default(60))
    ),
    /**
     * When set media library items are retrieved with role-specific API keys
     */
    MEDIA_API_KEYS: z.preprocess((x: unknown) => {
      if (typeof x === 'string') {
        const v = JSON.parse(x);
        if (!v) {
          return v;
        }
        const ret = {} as Record<string, string>;
        Object.keys(v).forEach((k) => {
          const sortedKey = k
            .split('|')
            .sort((a: string, b: string) => a.localeCompare(b))
            .join('|');
          ret[sortedKey] = v[k];
        });
        return ret;
      }
      return x;
    }, z.record(z.string()).optional()),
    /**
     * When site error pages are loaded on the browser (keeping the url static)
     */
    MEDIA_FANCY_ERRORS: z.preprocess(preprocessors.truthyBool, z.boolean().default(false)),
    /**
     * Whether or not nextauth debug mode is enabled
     */
    NEXTAUTH_DEBUG: schema(z.preprocess(preprocessors.truthyBool, z.boolean().default(false))),
    /**
     * Whether or not nextauth debug mode is enabled
     */
    NEXTAUTH_SECRET: schema(z.string().default('')),
    /**
     * Whether or not nextauth debug mode is enabled
     */
    NEXTAUTH_URL: schema(
      z.preprocess(
        preprocessors.urlWithoutTrailingSlash,
        z.string().default(envSource.PUBLIC_URL ?? '')
      )
    ),
    /**
     * Whether Dynatrace RUM is enabled
     */
    NEXT_PUBLIC_RUM_ENABLED: z.preprocess(preprocessors.truthyBool, z.boolean().default(false)),
    /**
     * Dynatrace Tenant ID
     */
    NEXT_PUBLIC_DYNATRACE_TENANT_ID: z.string().default('176fb25782e'),
    /**
     * Dynatrace Application ID
     */
    NEXT_PUBLIC_DYNATRACE_APPLICATION_ID: z.string().default('bf39931tga'),
    /**
     * Dynatrace Instance ID
     */
    NEXT_PUBLIC_DYNATRACE_INSTANCE_ID: z.string().default('fa750048aecb7b35'),
    /**
     * Highlight Observability Project ID
     */
    NEXT_PUBLIC_HIGHLIGHT_PROJECT_ID: schema(z.string().optional()),
    /**
     * OKTA API token
     */
    OKTA_APITOKEN: schema(z.string().default('NO ACCESS')),
    /**
     * Base URL for OKTA - inferred from OKTA_ISSUER
     */
    OKTA_BASEURL: schema(
      z.preprocess(() => {
        try {
          const url = new URL(process.env.OKTA_ISSUER ?? '');
          return preprocessors.urlWithoutTrailingSlash(url.origin);
        } catch (error) {
          return '';
        }
      }, z.string().default(''))
    ),
    /**
     * Name of the claim used to authorize website access.
     */
    OKTA_CLAIM: schema(z.string().default('authz_beconnected')),
    /**
     * OKTA client id
     */
    OKTA_CLIENTID: schema(z.string().default('')),
    /**
     * OKTA client id
     */
    OKTA_CLIENTSECRET: schema(z.string().default('')),
    /**
     * When set identifies the name of the claim containing center data
     */
    OKTA_CLAIM_CENTERS: schema(z.string().default('')),
    /**
     * OKTA issuing endpoint
     */
    OKTA_ISSUER: schema(
      z.preprocess(preprocessors.urlWithoutTrailingSlash, z.string().default(''))
    ),
    /**
     * OKTA authentication scope
     */
    OKTA_SCOPE: schema(z.string().default('api_beconnected')),
    /**
     * Highlight Observability API key - see https://www.hyperdx.io/search
     */
    PUBLIC_HYPERDX_API_KEY: schema(z.string().optional()),
    /**
     * Public URL for site
     */
    PUBLIC_URL: schema(z.preprocess(preprocessors.urlWithoutTrailingSlash, z.string().url())),
    /**
     * If specified, a password the search bot can use to authenticate.
     */
    SEARCH_CRAWLER_PASSWORD: schema(z.string().optional()),
    /**
     * Packaged app name
     */
    JSS_APP_NAME: schema(z.string().min(1)),
    /**
     * Packaged app name normalized to remove preview flag if present.
     * Inferred from JSS_APP_NAME
     */
    JSS_APP_NAME_NO_PREVIEW: schema(
      z.preprocess(() => {
        try {
          let jssAppName = process.env.JSS_APP_NAME ?? '';
          // If the app name starts with 'Preview_' remove it.
          if (jssAppName.toLocaleLowerCase().startsWith('preview_')) {
            jssAppName = jssAppName.substring(8);
          }
          return jssAppName;
        } catch (error) {
          return '';
        }
      }, z.string().default(''))
    ),
    /**
     * Secret used to authenticate requests from content manangement server
     */
    JSS_EDITING_SECRET: schema(z.string()),
    /**
     * Whether or not authentication is enabled for this site
     */
    SKIP_AUTHENTICATION: schema(z.preprocess(preprocessors.truthyBool, z.boolean().default(false))),
    /**
     * Session timeout
     */
    SESSION_TIMEOUT: schema(z.number().default(15)),
    /**
     * Base URL of sitecore API host.  Always ends with a trailing slash.
     */
    SITECORE_API_HOST: schema(z.preprocess(preprocessors.urlWithTrailingSlash, z.string().url())),

    /**
     * Sitecore API key
     */
    SITECORE_API_KEY: schema(
      z
        .string()
        .regex(guidMatch, 'Must be a GUID value')
        .transform((x, ctx) => {
          if (typeof window === 'undefined') {
            return x;
          }
          const match = guidMatch.exec(x.trim());
          if (!match || match.length < 1) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Sitecore API keys must be GUIDs (input ${x} ${JSON.stringify(match)})`,
              fatal: true,
            });
            return z.NEVER;
          }
          return `{${match[1]}}`;
        })
    ),
    /**
     * Sitecore API key
     */
    SITECORE_API_KEY_REPORTS: schema(
      z
        .string()
        .default('')
        .transform((x, ctx) => {
          if (!x) {
            return x;
          }
          const match = guidMatch.exec(x.trim());
          if (!match || match.length < 1) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Sitecore API keys must be GUIDs (input ${x} ${JSON.stringify(match)})`,
              fatal: true,
            });
            return z.NEVER;
          }
          return `{${match[1]}}`;
        })
    ),
    /**
     * Whether or not 'vercel mode' (eg https) is enabled.  Pretty muchy always true
     */
    VERCEL: z.preprocess(preprocessors.truthyBool, z.boolean().default(false)),
  })
  .parse(envSource);
